import type { TourStep } from '@/components/ui/Tour';
import type { StartTourWithCompletionKeyOptions } from '@/components/tour/PageTourHost';
import type { PageTourKey } from './registry';
import {
  clubMembersRosterTourSteps,
  clubMembersSettingsTourSteps,
  clubMembersTypesTourSteps,
  type ClubMembersTourOptions,
  getClubMembersTourSteps,
} from './club-members-tour-steps';
import { schedulePageTourStart, scheduleTourStepsStart } from './scheduleAutoPageTour';

export type { ClubMembersTourOptions };

export type ClubMembersTourTab = 'members' | 'types' | 'addons' | 'settings';

export const CLUB_MEMBERS_TOUR_KEY: PageTourKey = 'club-members';

export const CLUB_MEMBERS_TAB_TOUR_KEYS: Partial<Record<ClubMembersTourTab, PageTourKey>> = {
  members: 'club-members-roster',
  types: 'club-members-types',
  settings: 'club-members-settings',
};

export function resolveClubMembersTabTourKey(tab: ClubMembersTourTab): PageTourKey | null {
  return CLUB_MEMBERS_TAB_TOUR_KEYS[tab] ?? null;
}

export interface ClubMembersTabHelpContent {
  title: string;
  description: string;
  tourLabel?: string;
  tourDescription?: string;
}

export const CLUB_MEMBERS_PAGE_HELP = {
  title: 'Members',
  description: 'Manage your member roster, membership plans, and club membership settings.',
  tourLabel: 'Tour this page',
  tourDescription:
    'Walk through each tab, membership plans, settings, and creating a plan.',
};

export const CLUB_MEMBERS_TAB_HELP: Record<ClubMembersTourTab, ClubMembersTabHelpContent> = {
  members: {
    title: 'Members',
    description: 'Search the roster, assign memberships, and manage member households.',
  },
  types: {
    title: 'Membership plans',
    description: 'Create and manage plans members can purchase or be assigned to.',
  },
  addons: {
    title: 'Add-ons',
    description:
      'Optional extras members can purchase with a plan, such as locker hire or league competitions. Configure them from the Membership plans tab.',
  },
  settings: {
    title: 'Membership settings',
    description: 'Configure online join and club membership year settings.',
  },
};

export function getClubMembersTabHelp(tab: ClubMembersTourTab): ClubMembersTabHelpContent {
  return CLUB_MEMBERS_TAB_HELP[tab];
}

export function getClubMembersTabTourSteps(tab: ClubMembersTourTab): TourStep[] {
  switch (tab) {
    case 'members':
      return clubMembersRosterTourSteps;
    case 'types':
      return clubMembersTypesTourSteps;
    case 'settings':
      return clubMembersSettingsTourSteps;
    default:
      return clubMembersRosterTourSteps;
  }
}

export function scheduleClubMembersTourStart(
  startTourWithCompletionKey: (
    completionKey: string,
    steps: TourStep[],
    options?: StartTourWithCompletionKeyOptions,
  ) => void,
  options: ClubMembersTourOptions = {},
): () => void {
  const steps = getClubMembersTourSteps(options);
  return scheduleTourStepsStart(
    () =>
      startTourWithCompletionKey(CLUB_MEMBERS_TOUR_KEY, steps, {
        onFinish: options.onFinish,
      }),
    steps,
  );
}

export function scheduleClubMembersTabTourStart(
  startTourWithCompletionKey: (completionKey: string, steps: TourStep[]) => void,
  tab: ClubMembersTourTab,
  onEnterTab?: () => void,
): () => void {
  const tourKey = resolveClubMembersTabTourKey(tab);
  if (!tourKey) {
    return () => {};
  }
  const steps = getClubMembersTabTourSteps(tab).map((step, index) => {
    if (index === 0 && onEnterTab) {
      return { ...step, onStepEnter: onEnterTab };
    }
    return step;
  });
  return schedulePageTourStart(
    () => startTourWithCompletionKey(tourKey, steps),
    tourKey,
  );
}
